import { CommunityTransactionForPurchaseQuery, CompanyParametersQuery, CurrencyCollectionQuery, PurchaseCommunityTransactionSymbol, UnitQuery, VatRateForPurchaseQuery, VatRateItem, VatRatePLSymbol, sortVatRates } from '@skylab-shared/business';
import { LocalContext, ModelBase } from '@skylab-shared/core';
import { CalculationMethod, DiscountKind, ProductModel, PurchaseInvoiceDocumentItemModel, PurchaseInvoiceDocumentModel } from '@skylab-shared/dataModel';
import { RecursiveDiff } from '@skylab-shared/types';
import { Calc, MidpointRounding, head } from '@skylab-shared/utils';

export const PurchaseItemQuantityPrecision = 6;

export async function refreshPurchaseInvoiceDocumentItemPriceAsync(
    item: PurchaseInvoiceDocumentItemModel,
    localContext: LocalContext,
    model: PurchaseInvoiceDocumentModel,
    product: ProductModel
): Promise<void> {
    if (!item.productId)
        return;

    const { precision } = localContext.firstPreloaded(CurrencyCollectionQuery, { id: model.currencyId });
    const { systemCurrency } = localContext.getPreloaded(CompanyParametersQuery);
    const systemCurrencyId = localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency }).id;

    const multiplier = (model.currencyId !== systemCurrencyId) && model.exchangeRate ? (model.exchangeRate.rate || 1) / (model.exchangeRate.units || 1) : 1;

    const price = product.purchasePrice || { net: 0, gross: 0 };
    switch (model.calculationMethod) {
        case CalculationMethod.Net:
        case CalculationMethod.Manual: {
            const newPrice = (systemCurrencyId === model.currencyId) ? price.net : (price.net / multiplier);
            item.price_netBeforeDiscount = Calc.round(newPrice, precision, MidpointRounding.AwayFromZero);
        } break;

        case CalculationMethod.Gross: {
            const newPrice = (systemCurrencyId === model.currencyId) ? price.gross : (price.gross / multiplier);
            item.price_grossBeforeDiscount = Calc.round(newPrice, precision, MidpointRounding.AwayFromZero);
        } break;
    }

    if (item.discountKind === DiscountKind.Quota) {
        const newDiscountValue = item.discountValue / multiplier;
        item.discountValue = Calc.round(newDiscountValue, precision, MidpointRounding.AwayFromZero);
    }
}

export async function fillPurchaseInvoiceDocumentProductPropertiesAsync(
    args: { id: number, name: string },
    localContext: LocalContext,
    item: PurchaseInvoiceDocumentItemModel,
    model: PurchaseInvoiceDocumentModel,
    product: ProductModel
): Promise<void> {
    item.productId = args.id || null;
    item.discountKind = DiscountKind.Percentage;

    item.vatRateId = getPurchaseInvoiceItemVatRateId(localContext, model, product);

    item.unitId = product.unitId || (localContext.firstPreloaded(UnitQuery, { default: true })).id;
    item.cnCode = product.cnCode;
    item.symbol = product.symbol;
    item.barCode = product.barCode;
    item.jpkVatGroup = head(product.jpkVatGroups) ?? null;
    item.productName = args.name;
    item.pkwiu = product.pkwiu;
    item.includesInventoryControl = product.inventoryControl;
    item.inventoryLevel = product.inventoryControl ? product.inventoryLevel ?? 0 : null;
    item.quantity = 1;
    item.price_netBeforeDiscount = 0;
    item.price_grossBeforeDiscount = 0;
}


export function getPurchaseInvoiceItemVatRateId(localContext: LocalContext, model: PurchaseInvoiceDocumentModel, product: Pick<RecursiveDiff<ProductModel, ModelBase>, 'purchaseVatRateId'>): number {
    const communityTransactions = localContext.getPreloaded(CommunityTransactionForPurchaseQuery);
    const vatRates = localContext.getPreloaded(VatRateForPurchaseQuery);
    const isZKTransaction = model.communityTransactionId === communityTransactions.find(elem => elem.symbol === 'ZK')?.id;
    const productVatRate = vatRates.find(elem => elem.id === product.purchaseVatRateId);

    if (isZKTransaction) {
        return productVatRate?.id || (model.issuedByVatPayer ? vatRates.find(vatRate => vatRate.default)?.id : vatRates.find(vatRate => vatRate.exempt)?.id)!;
    } else {
        return getPurchaseInvoiceItemVatRateForNotZkTransaction(localContext, model.communityTransactionId).id;
    }
}

export function getPurchaseInvoiceVatRates(communityTransactionId: number, localContext: LocalContext): VatRateItem[] {
    const communityTransactions = localContext.getPreloaded(CommunityTransactionForPurchaseQuery);
    const isZKTransaction = communityTransactionId === communityTransactions.find(elem => elem.symbol === 'ZK')?.id;
    const vatRates = localContext.getPreloaded(VatRateForPurchaseQuery);

    return sortVatRates(isZKTransaction ? vatRates : [getPurchaseInvoiceItemVatRateForNotZkTransaction(localContext, communityTransactionId)]);
}

function getPurchaseInvoiceItemVatRateForNotZkTransaction(localContext: LocalContext, communityTransactionId: number): VatRateItem {
    const communityTransactions = localContext.getPreloaded(CommunityTransactionForPurchaseQuery);
    const vatRates = localContext.getPreloaded(VatRateForPurchaseQuery);

    const vatRate0 = vatRates.find(elem => (elem.symbol as VatRatePLSymbol) === '0%');
    const vatRateNieop = vatRates.find(elem => (elem.symbol as VatRatePLSymbol) === 'nieop.');

    const x: Partial<Record<PurchaseCommunityTransactionSymbol, VatRateItem>> = {
        IU: vatRateNieop,
        WNT: vatRate0,
        IT: vatRate0
    };

    return x[communityTransactions.find(elem => elem.id === communityTransactionId)?.symbol!]!;
}
